import { addLengthHeader, getConnectionURL } from './data-transfer.utils';
import { AlkLogger } from '@alk/core';
import { BehaviorSubject, NEVER, Observable, Subject, Subscription } from 'rxjs';
import { CONNECTION_STATE, EConnectionState } from '../../enums';
import { IDataTransferService } from './data-transfer.interface';
import { injectable } from 'inversify';
import { switchMap, tap } from 'rxjs/operators';
import { uInt8ArrayToLog } from '../../helpers/uint8array-utils';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

@injectable()
export class DataTransferService implements IDataTransferService {

    private socket$: WebSocketSubject<Uint8Array>;
    private readonly onOpen = new Subject();
    private readonly onClose = new Subject();
    private readonly connectionObs$ = new BehaviorSubject<EConnectionState>('connected');
    private defaultSubscription: Subscription;
    private openSubscription: Subscription;
    private closeSubscription: Subscription;
    private _messageObs$: Observable<Uint8Array>;

    send(message: Uint8Array): void {
        AlkLogger.info(`[DataTransferService#send] About to send payload: ${ ''
        } ${ uInt8ArrayToLog(message) }, result: ${ uInt8ArrayToLog(addLengthHeader(message)) }`);

        if (Boolean(this.socket$)) {
            this.socket$.next(message);
        } else {
            AlkLogger.error('[DataTransferService#send] attempt to send in empty socket.');
        }
    }

    async open(): Promise<void> {
        AlkLogger.info('[DataTransferService#open] is about to open');

        if (!Boolean(this.socket$)) {
            this.socket$ = this.webSocketFabric();

            AlkLogger.info('[DataTransferService#open] connection created');

            this.openSubscription = this.onOpen.subscribe(() => this.connectionObs$.next('connected'));

            this.closeSubscription = this.onClose.subscribe(() => {
                AlkLogger.info('[DataTransferService#close] websocket closed');
                this.close();
            });

            this.socket$.subscribe();
        }
    }

    close = (): void => {

        AlkLogger.info('[DataTransferService#close] destroyed');

        this.socket$?.complete();
        this.socket$?.unsubscribe();
        this.defaultSubscription?.unsubscribe();
        this.openSubscription?.unsubscribe();
        this.closeSubscription?.unsubscribe();

        delete this.socket$;
        delete this.defaultSubscription;
        delete this.openSubscription;
        delete this.closeSubscription;

        this.connectionObs$.next('disconnected');
    };

    messageObserver(): Observable<Uint8Array> {
        return this._messageObs$ ?? (this._messageObs$ = this.connectionObs$.pipe(
            tap(state => AlkLogger.info(`[DataTransferService#open] WebSocket connection is '${ CONNECTION_STATE[ state ] }'`)),
            switchMap(() => this.socket$?.asObservable() ?? NEVER),
        ));
    }

    connectionStateObserver = (): Observable<EConnectionState> => this.connectionObs$.asObservable();

    private webSocketFabric() {
        const uri = getConnectionURL();

        AlkLogger.info(`[DataTransferService#webSocketFabric] create ws to '${ uri }'`);

        return webSocket<Uint8Array>({
            url: uri,
            binaryType: 'arraybuffer',
            serializer: data => addLengthHeader(data),
            deserializer: event => new Uint8Array(event.data),
            openObserver: this.onOpen,
            closeObserver: this.onClose,
        });
    }
}
